import React, { Fragment } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setService, resetService } from "rtkSlices/ServiceCreatorSlice";
import { useSnackbar } from "notistack";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Monitoring from "../Monitoring";
import Controlling from "../Controlling";

// Merged "Monitoring & Controls" step — collapses the former Monitoring (raw page
// 2), Controlling (3), and Value Insights (4) wizard steps into one tabbed step.
// INDEX-PRESERVING (Option A): the raw page integer 2/3/4 still selects the active
// sub-tab, so no downstream step-index assumption moves.
// The children render EMBEDDED (their Cards picker + add-popups only; their own nav
// rows hidden), and this wrapper owns the single Back / Next (/ Cancel / Save) row
// plus the consolidated validation (create mode only - in edit mode the editor's
// save runs the checks once, gated by the held parts, D28).
//
// key={page} forces a remount when switching between the datapoint (2) and value-
// insight (4) tabs so Monitoring rebuilds its type-filtered list.
const TABS = [
  { page: 2, label: "Monitoring" },
  { page: 3, label: "Controlling" },
  { page: 4, label: "Value Insights" },
];

export default function MonitoringControls(props) {
  const dispatch = useDispatch();
  const serviceValue = useSelector((s) => s.serviceCreator);
  const { enqueueSnackbar } = useSnackbar();
  const page = serviceValue.page;
  const activeIndex = page === 3 ? 1 : page === 4 ? 2 : 0;

  const snack = (title, message, variant, timeOut) =>
    enqueueSnackbar(
      { title, message: message || "Something went wrong", variant },
      { timeOut }
    );

  // Consolidated validation (all three former per-step gates run once here).
  function validate() {
    let missing = false;
    serviceValue.asset.forEach((assetId) => {
      const m = serviceValue.assetMapping.find((a) => a.assetId === assetId);
      if (!m || !m.sensors || m.sensors.length === 0) {
        if (!missing) {
          missing = true;
          snack("Solution", "Please select atleast 1 datapoint for each asset", "error", 1000);
        }
      }
    });
    if (missing) return false;
    if (serviceValue.datapoints.length < 1) {
      snack("Datapoint", "Datapoint is required", "info", 1000);
      return false;
    }
    if (serviceValue.actuator.length < 1 && serviceValue.monitoring.length < 1) {
      snack("Solution", "Select atleast one Monitoring/Controlling parameter", "info", 1000);
      return false;
    }
    return true;
  }

  const cancel = () => {
    // Editor exit (matches Layout/Dashboard Cancel).
    dispatch(resetService());
    if (props.setSelected) props.setSelected(null);
  };
  const back = () => dispatch(setService({ page: 1 })); // Asset
  const next = () => {
    // Edit mode: no step-level pre-check (D28) - a partial editor must be able to move on.
    if (props.edit || validate()) dispatch(setService({ page: 5 })); // Configurations
  };
  const save = () => props.handleSave();

  const childProps = {
    edit: props.edit,
    setSelected: props.setSelected,
    handleSave: props.handleSave,
    embedded: true,
  };
  const active =
    page === 3 ? (
      <Controlling key={page} {...childProps} readOnly={props.controllingReadOnly} />
    ) : (
      <Monitoring
        key={page}
        {...childProps}
        readOnly={props.sensorsReadOnly}
        type={page === 4 ? "valueInsight" : "datapoint"}
      />
    );

  return (
    <Fragment>
      <Tabs
        value={activeIndex}
        centered
        onChange={(e, i) => dispatch(setService({ page: TABS[i].page }))}
        textColor="secondary"
        indicatorColor="secondary"
      >
        {TABS.map((t) => (
          <Tab key={t.page} label={t.label} />
        ))}
      </Tabs>
      <div>{active}</div>
      <Divider />
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          position: "relative",
          top: "10px",
        }}
      >
        {props.edit ? (
          <Button color="error" onClick={cancel}>
            Cancel
          </Button>
        ) : null}
        <Button color="secondary" onClick={back}>
          Back
        </Button>
        <Button color="secondary" onClick={next}>
          Next
        </Button>
        {props.edit && props.handleSave ? (
          <Button color="secondary" onClick={save}>
            Save
          </Button>
        ) : null}
      </div>
    </Fragment>
  );
}
